// Port "3D-Text" — extrudierte 3D-Typografie (text3d/number3d-Primitive) aus
// Jans Renderer.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/RendererWebGL.ts — drei REINE Methoden
// (makeText3dGeometry, glyphToShapePath, number3dString, 113 Zeilen, 0
// this.-Referenzen) plus die 3D-Font-Aufloesung aus assets/fonts.ts (FONTS_3D
// + font3dUrl).
//
// WAS DAS IST
//
// text3d/number3d sind Object3D-PRIMITIVE (content.primitive). Anders als der
// flache `text`-Layer (troika) baut das hier eine extrudierte Glyphen-Geometrie
// aus einer Typeface-JSON (three FontLoader). number3d zaehlt ueber die
// Layer-Lebensdauer hoch; die Ziffern werden pro Bild neu gebaut (renderLayer).
//
// GATE (MERGE-GATE-BEFUND 2026-07-22): kein Bestands-Scrolly nutzt text-3d.
// Der guardete Zweig in updateObject/renderLayer feuert fuer keinen von ihnen
// — additiv-sicher.
//
// Die drei Geometrie-Funktionen sind unveraendert aus Jan uebernommen (einzige
// Umformung: `this.glyphToShapePath` -> freie Funktion). Kanonisch gegenpruefbar.
import THREE, { mergeGeometries } from "../utils/THREE";

/** Typeface-Key -> gebuendelte Typeface-JSON. 1:1 aus Jans FONTS_3D.
 *  Die Dateien liegen in public/public/fonts3d/ (Vite serviert sie unter /). */
export const FONTS_3D: Record<string, { label: string; url: string }> = {
    droidSans: { label: "Droid Sans", url: "/fonts3d/droid_sans_regular.typeface.json" },
    robotoCondensed: { label: "Roboto Condensed", url: "/fonts3d/roboto_condensed.typeface.json" },
    sourceSans3: { label: "Source Sans 3", url: "/fonts3d/source_sans_3.typeface.json" },
    notoSans: { label: "Noto Sans", url: "/fonts3d/noto_sans.typeface.json" },
    sofiaSans: { label: "Sofia Sans Semi Condensed", url: "/fonts3d/sofia_sans_semi_condensed.typeface.json" },
    merriweather: { label: "Merriweather", url: "/fonts3d/merriweather.typeface.json" },
    notoSerif: { label: "Noto Serif", url: "/fonts3d/noto_serif.typeface.json" },
    sourceSerif4: { label: "Source Serif 4", url: "/fonts3d/source_serif_4.typeface.json" },
    portLligatSlab: { label: "Port Lligat Slab", url: "/fonts3d/port_lligat_slab.typeface.json" },
    jetBrainsMono: { label: "JetBrains Mono", url: "/fonts3d/jetbrains_mono.typeface.json" },
    italianno: { label: "Italianno", url: "/fonts3d/italianno.typeface.json" },
    // Fette schmale Grotesk (Jans Wahl 06.10.2026): Barlow Semi Condensed,
    // Barlow Condensed, Oswald — je eigener Schnitt, 3D kennt keine Stärke.
    barlowSemiCondensedBold: {
        label: "Barlow Semi Condensed Bold",
        url: "/fonts3d/barlow_semi_condensed_bold.typeface.json",
    },
    barlowSemiCondensedBlack: {
        label: "Barlow Semi Condensed Black",
        url: "/fonts3d/barlow_semi_condensed_black.typeface.json",
    },
    barlowCondensedBold: { label: "Barlow Condensed Bold", url: "/fonts3d/barlow_condensed_bold.typeface.json" },
    barlowCondensedExtraBold: {
        label: "Barlow Condensed ExtraBold",
        url: "/fonts3d/barlow_condensed_extrabold.typeface.json",
    },
    oswaldSemiBold: { label: "Oswald SemiBold", url: "/fonts3d/oswald_semibold.typeface.json" },
    oswaldBold: { label: "Oswald Bold", url: "/fonts3d/oswald_bold.typeface.json" },
};

export const DEFAULT_FONT_3D = "droidSans";

/** Aufloesung eines primitiveParams.font-Keys zur Typeface-URL; unbekannte/
 *  Legacy-Keys fallen auf die Default-Schrift zurueck. */
export const font3dUrl = (key?: string): string =>
    (key && FONTS_3D[key]?.url) || FONTS_3D[DEFAULT_FONT_3D].url;

export function glyphToShapePath(glyph: TODOAny, scale: number, offsetX: number): TODOAny {
  const path = new THREE.ShapePath()
  let x = 0
  let y = 0
  const outline =
    glyph._cachedOutline || (glyph._cachedOutline = glyph.o.split(' '))
  for (let i = 0, l = outline.length; i < l; ) {
    switch (outline[i++]) {
      case 'm':
        x = outline[i++] * scale + offsetX
        y = outline[i++] * scale
        path.moveTo(x, y)
        break
      case 'l':
        x = outline[i++] * scale + offsetX
        y = outline[i++] * scale
        path.lineTo(x, y)
        break
      case 'q': {
        const cpx = outline[i++] * scale + offsetX
        const cpy = outline[i++] * scale
        const cpx1 = outline[i++] * scale + offsetX
        const cpy1 = outline[i++] * scale
        path.quadraticCurveTo(cpx1, cpy1, cpx, cpy)
        break
      }
      case 'b': {
        const cpx = outline[i++] * scale + offsetX
        const cpy = outline[i++] * scale
        const cpx1 = outline[i++] * scale + offsetX
        const cpy1 = outline[i++] * scale
        const cpx2 = outline[i++] * scale + offsetX
        const cpy2 = outline[i++] * scale
        path.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, cpx, cpy)
        break
      }
    }
  }
  return path
}

export function makeText3dGeometry(str: string, p: TODOAny, font: TODOAny): TODOAny {
  const bevel = p.bevel ?? 0
  const data = font.data
  const size = p.size ?? 300
  const resolution = data.resolution || 1000
  const scale = size / resolution
  // extra tracking between glyphs, given as a percentage of the font size
  const tracking = ((p.letterSpacing ?? 0) / 100) * size
  const text = str.length ? str : ' '
  const shapes: TODOAny[] = []
  let penX = 0
  let prev: string | null = null
  for (const ch of Array.from(text) as string[]) {
    const glyph = data.glyphs[ch] || data.glyphs['?']
    if (!glyph) {
      prev = null
      continue
    }
    // kerning: pull the current glyph towards the previous one (font units →
    // same space as the glyph coords, so scale identically)
    if (prev != null) {
      const k = data.kerning && data.kerning[prev] && data.kerning[prev][ch]
      if (k) penX += k * scale
    }
    if (glyph.o) {
      const path = glyphToShapePath(glyph, scale, penX)
      shapes.push(...path.toShapes(false))
    }
    penX += (glyph.ha || 0) * scale + tracking
    prev = ch
  }
  const geometry = new THREE.ExtrudeGeometry(shapes, {
    depth: p.depth ?? 60,
    curveSegments: p.curveSegments ?? 6,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel * 0.6,
    bevelSegments: 3,
  })
  geometry.computeBoundingBox()
  geometry.center()
  return geometry
}

/**
 * SCHNELLER AUFBAU FUER PER-FRAME-TEXT (Jans Fund am Welcome-Scrolly,
 * 07./08.09.2026, gemessen: Counter-Abschnitt 35-43 ms pro Bild statt 12).
 *
 * `makeText3dGeometry` extrudiert bei JEDEM Aufruf jede Glyphe neu (Kurven-
 * Unterteilung, Fase, Triangulation) — beim Counter also bei jeder Ziffer,
 * beim text3d bei jeder animierten Laufweite. Hier wird jede Glyphe genau
 * EINMAL extrudiert (Cache pro Typeface + Groesse/Tiefe/Fase/Glaettung) und
 * der Text pro Bild nur noch aus verschobenen Kopien zusammengesetzt. Das
 * Zusammensetzen kopiert nur Zahlen-Arrays (unter einer Millisekunde), die
 * teure Geometrie-Erzeugung faellt aus dem Bildtakt.
 *
 * Ergebnis ist geometrisch identisch zu makeText3dGeometry: gleiche Pen-
 * Positionen (Kerning + Laufweite), gleiche Zentrierung ueber den ganzen
 * String. Einziger Unterschied: Glyphen werden einzeln extrudiert, nicht als
 * gemeinsame Shape-Liste — ExtrudeGeometry behandelt sie ohnehin getrennt.
 */
const glyphCache = new WeakMap<object, Map<string, TODOAny>>()

function cachedGlyphGeometry(ch: string, p: TODOAny, font: TODOAny, scale: number): TODOAny | null {
  const data = font.data
  const glyph = data.glyphs[ch] || data.glyphs['?']
  if (!glyph || !glyph.o) return null
  const bevel = p.bevel ?? 0
  const key = `${ch}|${p.size ?? 300}|${p.depth ?? 60}|${p.curveSegments ?? 6}|${bevel}`
  let perFont = glyphCache.get(font)
  if (!perFont) {
    perFont = new Map()
    glyphCache.set(font, perFont)
  }
  let geo = perFont.get(key)
  if (!geo) {
    const path = glyphToShapePath(glyph, scale, 0)
    geo = new THREE.ExtrudeGeometry(path.toShapes(false), {
      depth: p.depth ?? 60,
      curveSegments: p.curveSegments ?? 6,
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel * 0.6,
      bevelSegments: 3,
    })
    perFont.set(key, geo)
  }
  return geo
}

export function makeText3dGeometryFast(str: string, p: TODOAny, font: TODOAny): TODOAny {
  const data = font.data
  const size = p.size ?? 300
  const resolution = data.resolution || 1000
  const scale = size / resolution
  const tracking = ((p.letterSpacing ?? 0) / 100) * size
  const text = str.length ? str : ' '
  const parts: TODOAny[] = []
  let penX = 0
  let prev: string | null = null
  for (const ch of Array.from(text) as string[]) {
    const glyph = data.glyphs[ch] || data.glyphs['?']
    if (!glyph) {
      prev = null
      continue
    }
    if (prev != null) {
      const k = data.kerning && data.kerning[prev] && data.kerning[prev][ch]
      if (k) penX += k * scale
    }
    const base = cachedGlyphGeometry(ch, p, font, scale)
    if (base) {
      const copy = base.clone()
      copy.translate(penX, 0, 0)
      parts.push(copy)
    }
    penX += (glyph.ha || 0) * scale + tracking
    prev = ch
  }
  if (!parts.length) return new THREE.BufferGeometry()
  const geometry = parts.length === 1 ? parts[0] : mergeGeometries(parts, false)
  if (parts.length > 1) parts.forEach((g) => g.dispose())
  geometry.computeBoundingBox()
  geometry.center()
  return geometry
}

export function number3dString(p: TODOAny, progress: number): string {
  const start = Number(p.start ?? 0)
  const end = Number(p.end ?? 100)
  const decimals = Math.min(3, Math.max(0, Math.round(p.decimals ?? 0)))
  const value = start + (end - start) * progress
  let body = value.toFixed(decimals)
  // grouping separator: explicit thousandsSep, or '.' for the legacy boolean
  const sep: string = p.thousandsSep ?? (p.thousands ? '.' : '')
  if (sep) {
    const neg = body.startsWith('-')
    if (neg) body = body.slice(1)
    const [intPart, decPart] = body.split('.')
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
    const decimalSep = sep === '.' ? ',' : '.'
    body = (neg ? '-' : '') + grouped + (decPart ? decimalSep + decPart : '')
  }
  return `${p.prefix ?? ''}${body}${p.suffix ?? ''}`
}